import { RefObject, UIEvent, useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_BOTTOM_THRESHOLD_PX = 16;

export interface UseDetailActionsOverContentResult {
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    actionsOverContent: boolean;
    onScrollBody: (event: UIEvent<HTMLDivElement>) => void;
}

/**
 * Tracks whether a detail page scroll body still has content below the fold,
 * so floating action footers can switch between frosted and transparent styles.
 *
 * @param enabled When false, scroll tracking is skipped.
 * @param resetKey Change this (e.g. transaction id / loading) to remeasure after content loads.
 */
export function useDetailActionsOverContent(enabled: boolean, resetKey?: unknown): UseDetailActionsOverContentResult {
    const [actionsOverContent, setActionsOverContent] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const updateActionsOverContent = useCallback((element: HTMLDivElement) => {
        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        setActionsOverContent(distanceFromBottom > SCROLL_BOTTOM_THRESHOLD_PX);
    }, []);

    const onScrollBody = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            updateActionsOverContent(event.currentTarget);
        },
        [updateActionsOverContent]
    );

    useEffect(() => {
        if (!enabled) return;
        const element = scrollContainerRef.current;
        if (!element) return;
        updateActionsOverContent(element);
    }, [enabled, resetKey, updateActionsOverContent]);

    return {
        scrollContainerRef,
        actionsOverContent,
        onScrollBody
    };
}
